import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { CONTACT_METHOD_KEY, SMS_MESSAGE_BODY_MAX_LENGTH } from '@a1/shared';

import callAzureFunction from 'utils-ts/call-azure-function';
import handleError from 'utils-ts/handle-error';
import { resolveTemplateName } from 'hooks-ts/useGetEntity';

const SMS_SEGMENT_LENGTH = 153;

const formatSmsMessageBody = (messageBody = '') =>
  messageBody
    .split('\n')
    .map((messageLine) => messageLine.trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const SmsCommunicationMessageInput = ({
  communicationType,
  communicationSubtype,
  communicationEntityName,
  smsMessageBody,
  setSmsMessageBody
}) => {
  const appConfig = useSelector((state) => state.appConfig?.data);

  const [smsCommunicationTemplates, setSmsCommunicationTemplates] = useState([]);
  const [loadingSmsCommunicationTemplates, setLoadingSmsCommunicationTemplates] = useState(true);
  const [selectedSmsCommunicationTemplateId, setSelectedSmsCommunicationTemplateId] = useState('');

  const smsSegmentCount = Math.ceil(smsMessageBody.length / SMS_SEGMENT_LENGTH);
  const lowerCaseCommunicationEntityName = communicationEntityName.toLowerCase();

  useEffect(() => {
    const controller = new AbortController();

    const getSmsCommunicationTemplates = async () => {
      try {
        setLoadingSmsCommunicationTemplates(true);
        const response = await callAzureFunction({
          url: 'communication-templates',
          method: 'get',
          params: { page: 0, pageSize: 100, type: communicationType },
          signal: controller.signal
        });
        const communicationTemplates = response.data?.rows ?? [];
        setSmsCommunicationTemplates(
          communicationTemplates.filter(
            (communicationTemplate) =>
              String(communicationTemplate.contactMethod).toUpperCase() === CONTACT_METHOD_KEY.SMS &&
              (!communicationSubtype || communicationTemplate.subtype === communicationSubtype)
          )
        );
      } catch (error) {
        if (!controller.signal.aborted) {
          handleError(error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingSmsCommunicationTemplates(false);
        }
      }
    };

    getSmsCommunicationTemplates();

    return () => {
      controller.abort();
    };
  }, [communicationSubtype, communicationType]);

  const handleSmsCommunicationTemplateChange = (event) => {
    const communicationTemplateId = event.target.value;
    const selectedCommunicationTemplate = smsCommunicationTemplates.find(
      (communicationTemplate) => communicationTemplate.id === communicationTemplateId
    );
    setSelectedSmsCommunicationTemplateId(communicationTemplateId);
    setSmsMessageBody(formatSmsMessageBody(selectedCommunicationTemplate?.messageBody).slice(0, SMS_MESSAGE_BODY_MAX_LENGTH));
  };

  return (
    <Stack gap={2}>
      <Select
        fullWidth
        displayEmpty
        size="small"
        input={<OutlinedInput />}
        value={selectedSmsCommunicationTemplateId}
        onChange={handleSmsCommunicationTemplateChange}
        disabled={loadingSmsCommunicationTemplates}
        renderValue={(communicationTemplateId) => {
          const selectedCommunicationTemplate = smsCommunicationTemplates.find(
            (communicationTemplate) => communicationTemplate.id === communicationTemplateId
          );
          if (!selectedCommunicationTemplate) {
            return <Typography color="text.disabled">{`Select ${lowerCaseCommunicationEntityName} communication`}</Typography>;
          }
          return resolveTemplateName(selectedCommunicationTemplate.name, appConfig);
        }}
      >
        {smsCommunicationTemplates.map((communicationTemplate) => (
          <MenuItem key={communicationTemplate.id} value={communicationTemplate.id}>
            {resolveTemplateName(communicationTemplate.name, appConfig)}
          </MenuItem>
        ))}
      </Select>
      {!loadingSmsCommunicationTemplates && smsCommunicationTemplates.length === 0 && (
        <FormHelperText sx={{ ml: 1, mt: -1.5 }}>{`No SMS ${lowerCaseCommunicationEntityName} communications found.`}</FormHelperText>
      )}
      {selectedSmsCommunicationTemplateId && (
        <Stack gap={0.5}>
          <OutlinedInput
            fullWidth
            multiline
            minRows={4}
            maxRows={8}
            size="small"
            placeholder="Enter SMS message"
            value={smsMessageBody}
            onChange={(event) => setSmsMessageBody(event.target.value)}
            inputProps={{ maxLength: SMS_MESSAGE_BODY_MAX_LENGTH }}
          />
          <Stack direction="row" justifyContent="space-between" gap={2}>
            <FormHelperText sx={{ ml: 1, mt: 0 }}>
              {`Changes only apply to this message and will not update the ${lowerCaseCommunicationEntityName} communication.`}
            </FormHelperText>
            <FormHelperText sx={{ mt: 0, whiteSpace: 'nowrap' }}>
              {smsMessageBody.length}/{SMS_MESSAGE_BODY_MAX_LENGTH} chars · {smsSegmentCount} segment{smsSegmentCount === 1 ? '' : 's'}
            </FormHelperText>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

SmsCommunicationMessageInput.propTypes = {
  communicationType: PropTypes.string.isRequired,
  communicationSubtype: PropTypes.string,
  communicationEntityName: PropTypes.string.isRequired,
  smsMessageBody: PropTypes.string.isRequired,
  setSmsMessageBody: PropTypes.func.isRequired
};

export default SmsCommunicationMessageInput;
